"use client";

import { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import useEmblaCarousel from "embla-carousel-react";

interface PhotoGalleryProps {
  photos: { id: string; blurDataURL?: string; version?: number }[];
  alt: string;
  // Asset host, resolved at runtime by the server page (img.autobook.lt CDN on
  // prod, admin.<env> elsewhere). Passed as a prop because this is a client
  // component — process.env.NEXT_PUBLIC_* would be the value baked at build time.
  assetBaseUrl: string;
}

export default function PhotoGallery({ photos, alt, assetBaseUrl }: PhotoGalleryProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollTo = useCallback(
    (idx: number) => emblaApi?.scrollTo(idx),
    [emblaApi],
  );
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") scrollPrev();
      if (e.key === "ArrowRight") scrollNext();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [scrollPrev, scrollNext]);

  if (photos.length === 0) return null;

  const assetUrl = (photo: { id: string; version?: number }) =>
    `${assetBaseUrl}/assets/${photo.id}${photo.version ? `?v=${photo.version}` : ""}`;

  return (
    <div className="space-y-3">
      <div className="relative aspect-[16/10] bg-gray-100 rounded-xl overflow-hidden">
        <div className="absolute inset-0 overflow-hidden" ref={emblaRef}>
          <div className="flex h-full">
            {photos.map((photo, i) => (
              <div key={photo.id} className="relative h-full min-w-0 flex-[0_0_100%]">
                <Image
                  src={assetUrl(photo)}
                  alt={`${alt} — ${i + 1}`}
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 60vw"
                  quality={75}
                  priority={i === 0}
                  placeholder={photo.blurDataURL ? "blur" : "empty"}
                  blurDataURL={photo.blurDataURL}
                />
              </div>
            ))}
          </div>
        </div>

        {photos.length > 1 && (
          <>
            <button
              type="button"
              onClick={scrollPrev}
              aria-label="Ankstesnė nuotrauka"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-md flex items-center justify-center text-gray-800 transition"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={scrollNext}
              aria-label="Kita nuotrauka"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-md flex items-center justify-center text-gray-800 transition"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-md">
              {selectedIndex + 1} / {photos.length}
            </div>
          </>
        )}
      </div>

      {photos.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {photos.map((photo, i) => (
            <button
              key={photo.id}
              type="button"
              onClick={() => scrollTo(i)}
              aria-label={`Nuotrauka ${i + 1}`}
              className={`relative w-20 h-14 flex-shrink-0 rounded-md overflow-hidden border-2 transition ${
                selectedIndex === i
                  ? "border-blue-600"
                  : "border-transparent hover:border-gray-300"
              }`}
            >
              <Image
                src={assetUrl(photo)}
                alt={`${alt} — thumb ${i + 1}`}
                fill
                className="object-cover"
                sizes="80px"
                quality={50}
                placeholder={photo.blurDataURL ? "blur" : "empty"}
                blurDataURL={photo.blurDataURL}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
